import {
  AgentGroup,
  AgentGroupBatchPatchDto,
  AppPermission
} from "@closerplatform/spinner-openapi";
import { flow } from "lodash";
import React, { createContext, useContext, useEffect } from "react";
import { useStore } from "../../../models";
import { useAgentServices } from "../../../models/agentServices";
import { usePreloader } from "../../HOC/withPreloading/withPreloading";
import {
  ListHookEditable,
  ListHookWithFilters,
  ListHookWithSelectables,
  Params
} from "../models/useSettingsList/types";
import {
  useSettingsListEditable,
  withFilters,
  withSelectables
} from "../models/useSettingsList/useSettingsList";
import { ExistingAgentGroupForm, NewAgentGroupForm } from "./types";
import {
  copyExistingAgentGroupForm,
  createNewAgentGroupForm,
  mapAgentGroupToForm,
  mapExistingFormsToPatchForms,
  mapNewFormToCreateForm
} from "./utils";

const AgentGroupsSettingsContext = createContext<AgentGroupsSettingsProviderValue | undefined>(
  undefined
);
AgentGroupsSettingsContext.displayName = "AgentGroupsSettingsContext"; // for name to display in dev tools

type HookParams = Params<ExistingAgentGroupForm, NewAgentGroupForm, AgentGroup>;
type HookEditable = ListHookEditable<ExistingAgentGroupForm, NewAgentGroupForm>;
type HookSelectable = ListHookWithSelectables<
  ExistingAgentGroupForm,
  NewAgentGroupForm,
  HookEditable
>;
type HookFilterable = ListHookWithFilters<
  ExistingAgentGroupForm,
  NewAgentGroupForm,
  HookSelectable
>;

export type AgentGroupsSettingsProviderValue = HookFilterable;

const useListHook = flow<[HookParams], HookEditable, HookSelectable, HookFilterable>(
  useSettingsListEditable,
  withSelectables,
  withFilters
);

const useAgentGroupsSettingsState = () => {
  const { spinnerService } = useAgentServices();
  const { setLoadingState } = usePreloader();
  const { agentStore, currentUserStore } = useStore();

  const resetSelected = () => {
    listHook.deselectAll();
  };

  const load = async () => {
    // TODO: agentStore load profiles can be optional because its loaded while clicking advisers tab, or sth like lastUpdated and compare
    const [adviserGroups] = await Promise.all([
      spinnerService.getAgentGroups(),
      agentStore.loadAgentProfiles()
    ]);

    return adviserGroups;
  };

  const listHook = useListHook({
    apiGetItems: load,
    apiCreateItem: (form) => spinnerService.createAgentGroup(mapNewFormToCreateForm(form)),
    batchUpdateItems: async (items) => {
      const updateDtos = mapExistingFormsToPatchForms(items);
      const canEditGroups = !!currentUserStore.profile?.doesHavePermission(
        AppPermission.SettingsAgentGroupsConfiguration
      );
      const canEditAssignments = !!currentUserStore.profile?.doesHavePermission(
        AppPermission.SettingsAdvisersAssignments
      );

      const permissionRestrictedUpdateDtos: AgentGroupBatchPatchDto[] = updateDtos.map((u) => ({
        id: u.id,
        advisers: canEditGroups ? u.advisers : undefined,
        name: canEditGroups ? u.name : undefined,
        tags: canEditAssignments ? u.tags : undefined,
        tagGroups: canEditAssignments ? u.tagGroups : undefined
      }));

      await spinnerService.patchAgentGroups(permissionRestrictedUpdateDtos);
    },
    apiDeleteItem: (id) => spinnerService.deleteAgentGroup(id),
    mapItemToForm: mapAgentGroupToForm,
    createNewForm: createNewAgentGroupForm,
    copyExistingForm: copyExistingAgentGroupForm,

    saveErrorId: "settings.error.cantSave",
    onSubmitCallback: resetSelected,
    onDiscardCallback: resetSelected,
    onInitializeResult: setLoadingState
  });

  useEffect(() => {
    listHook.items.forEach((item) => {
      const setEdited = () => listHook.setItemEdited(item);
      item.$.tagIds.onUpdate(setEdited);
      item.$.tagGroupIds.onUpdate(setEdited);
      item.$.adviserIds.onUpdate(setEdited);
    });
  }, [listHook.items]);

  return listHook;
};

export const AgentGroupsSettingsProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const value = useAgentGroupsSettingsState();

  return (
    <AgentGroupsSettingsContext.Provider value={value}>
      {children}
    </AgentGroupsSettingsContext.Provider>
  );
};

export const useAgentGroupsSettings = (): AgentGroupsSettingsProviderValue => {
  const context = useContext(AgentGroupsSettingsContext);

  if (context === undefined) {
    throw new Error("useAgentGroupsSettings must be used within a AdviserGroupsSettingsProvider");
  }

  return context as AgentGroupsSettingsProviderValue;
};
